import { MicrosoftIcon } from '@common/components/brand/MicrosoftIcon';
import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { env } from '@config/env';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, FormField, Input, Separator } from '@ntconsult/aluma-react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks';
import { type LoginInput, loginSchema } from './LoginForm.schema';

export function LoginForm() {
  const { t } = useTranslation('auth');
  const { t: tErros } = useTranslation('erros');
  const { login, isLoggingIn, loginError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [params] = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  return (
    <div className="flex flex-col gap-5">
      {params.get('error') === 'auth_failed' && (
        <Alert variant="danger" title={t('azureCallbackFailed')} />
      )}

      <Alert variant="info" title={t('avisoEmailNt.titulo')}>
        {t('avisoEmailNt.texto')}
      </Alert>

      {env.VITE_AZURE_SSO_ENABLED && (
        <Button
          type="button"
          variant="secondary"
          fullWidth
          leftIcon={<MicrosoftIcon />}
          onClick={() => {
            window.location.href = `${env.VITE_API_URL}/auth/azure/start`;
          }}
        >
          {t('azureSignIn')}
        </Button>
      )}

      {env.VITE_AZURE_SSO_ENABLED && env.VITE_PASSWORD_LOGIN_ENABLED && (
        <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-foreground-subtle">
          <Separator decorative className="flex-1" />
          <span>{t('orContinueWith')}</span>
          <Separator decorative className="flex-1" />
        </div>
      )}

      {env.VITE_PASSWORD_LOGIN_ENABLED && (
        <form
          onSubmit={handleSubmit((data) => login(data))}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormField
            label={t('email')}
            hint={t('avisoEmailNt.dica')}
            error={errors.email?.message}
            required
          >
            <Input
              type="email"
              autoComplete="email"
              autoFocus
              leftIcon={<Mail />}
              {...register('email')}
            />
          </FormField>

          <FormField label={t('password')} error={errors.password?.message} required>
            <Input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              leftIcon={<Lock />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('passwordHide') : t('passwordShow')}
                  aria-pressed={showPassword}
                  className="flex h-full items-center justify-center px-1 text-foreground-subtle transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                </button>
              }
              {...register('password')}
            />
          </FormField>

          <div
            className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
              loginError ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className="min-h-0">
              {loginError && <Alert variant="danger" title={tErros(mensagemDeErro(loginError))} />}
            </div>
          </div>

          <Button type="submit" variant="primary" fullWidth loading={isLoggingIn}>
            {t('submit')}
          </Button>
        </form>
      )}
    </div>
  );
}
